import { useCallback, useEffect, useState } from 'react';
import { App, Button, Dropdown, Progress, Tag, Typography } from 'antd';

import DataState, { deriveDataState } from '../loading/DataState';
import Skeleton from '../loading/Skeleton';
import RecordBadge from '../common/RecordBadge';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getChangeCardTask, getChangeCardUndo, undoChangeCardTask } from '../../services/changeCard';
import { buildActionMenu } from '../common/actionMenu';
import { confirmDestructive } from '../common/confirmDestructive';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatCount, joinMeta } from '../../utils/counts';
import ChangeCardSourceOffer from './ChangeCardSourceOffer';
import { ARROW_COUNTERCLOCKWISE, ARROW_RIGHT, KEBAB } from '../icons';

const { Text } = Typography;

/**
 * Why an item a card change task did not move (keepr-api docs/SCHEMA.md §
 * Change card, "The move": the skip reasons), in the person's words.
 */
export const SKIP_REASON_WORDS = {
	changedSince: 'Changed since the change was reviewed',
	notFound: 'Deleted, or no longer there to move',
	otherCard: 'On another card already',
	outsideFamily: 'In a collection the new card is not used in',
	pending: 'Waiting for review',
	cannotEdit: 'Not editable by the person who moved them',
	locked: 'Locked',
	hiddenValues: 'Holds a value the person who moved them cannot see',
	restrictedValues: 'Holds a value only some people may change',
	itemTag: 'Used as a tag',
	skipped: 'Skipped',
};

const STATUS = {
	pending: { label: 'Moving…', color: 'processing' },
	failed: { label: 'Stopped', color: 'error' },
	cancelled: { label: 'Stopped', color: 'default' },
};

// Why a finished change cannot be undone now (keepr-api `changeCard.undoWhy`).
export const UNDO_WHY_WORDS = {
	undoing: 'An undo of this change is running.',
	undone: 'This change was undone.',
	nothing_moved: 'This change moved no item.',
	running: 'Undo it once it has finished.',
};

// The row's tag once an undo of it has started (KPR-704).
const UNDO_STATUS = {
	running: { label: 'Undoing…', color: 'processing' },
	done: { label: 'Undone', color: 'default' },
};

/** "120 moved · 3 skipped · 1 could not move" — or "Nothing moved yet". */
export function taskCountsLine( progress = {} ) {
	const line = joinMeta(
		progress.moved ? `${ progress.moved } moved` : null,
		progress.skipped ? `${ progress.skipped } skipped` : null,
		progress.failed ? `${ progress.failed } could not move` : null,
	);
	return line || ( progress.done ? 'Nothing moved' : 'Nothing moved yet' );
}

const progressOf = task => task?.progress || {};

/** "28 went back · 1 could not" — the undo's counts (KPR-704). */
export function undoCountsLine( progress = {} ) {
	const line = joinMeta(
		progress.reverted ? `${ progress.reverted } went back` : null,
		progress.failed ? `${ progress.failed } could not` : null,
	);
	return line || ( progress.done ? 'Nothing went back' : 'Nothing has gone back yet' );
}

/** What the confirm says undoing will do, in the cards' names. */
export function undoConfirmText( task = {} ) {
	const moved = task.progress?.moved || 0;
	const from = task.card?.name ? `“${ task.card.name }”` : 'the card they came from';
	const to = task.target?.name ? `“${ task.target.name }”` : 'the new card';
	return `The ${ formatCount( moved, 'item' ) } this change moved go back to ${ from }, each with every value it had there. Values entered on ${ to } since are kept in History. An item whose card changed again since is left as it is and listed.`;
}

/** "1200 of 30000 items worked through" while it runs. */
export function taskProgressLine( progress = {} ) {
	const total = progress.total || 0;
	return `${ progress.done || 0 } of ${ formatCount( total, 'item' ) || '0 items' } worked through. You can leave this page; it carries on.`;
}

function CardBadge( { card, cardsById } ) {
	if ( ! card ) return null;
	const record = ( card.id && cardsById?.[ card.id ] ) || { name: card.name };
	return <RecordBadge kind='card' record={record} title={record.name || card.name || 'A card'} size='xs' />;
}

/** What did not move, read when the person asks: skipped by reason, and each failure. */
function TaskReport( { collectionId, taskId } ) {
	const [ report, setReport ] = useState( null );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );

	const load = useCallback( async () => {
		setLoading( true );
		setError( null );
		try {
			setReport( await getChangeCardTask( collectionId, taskId ) );
		} catch ( e ) {
			setError( e );
		} finally {
			setLoading( false );
		}
	}, [ collectionId, taskId ] );

	useEffect( () => { load(); }, [ load ] );

	const skipped = Object.entries( report?.items?.skipped || {} ).filter( ( [ , n ] ) => n > 0 );
	const failures = report?.failures || [];
	const failed = report?.items?.failed || 0;
	const hasData = !! report;
	const isEmpty = hasData && ! skipped.length && ! failures.length;
	return (
		<DataState
			state={deriveDataState( { loading, error, hasData, isEmpty } )}
			hasData={hasData}
			isEmpty={isEmpty}
			skeleton={<Skeleton variant='line' count={2} />}
			empty={<Text type='secondary' className='imports-list__line'>Every item it worked through moved.</Text>}
			error={error}
			onRetry={load}
			label='what did not move'
			className='imports-task__report'
		>
			{skipped.length ? (
				<ul className='imports-plan__list'>
					{skipped.map( ( [ reason, n ] ) => (
						<li key={reason}>
							<Text>{formatCount( n, 'item' )}</Text>
							<Text type='secondary'>{` — ${ SKIP_REASON_WORDS[ reason ] || reason }`}</Text>
						</li>
					) )}
				</ul>
			) : null}
			{failures.length ? (
				<ul className='imports-plan__list'>
					{failures.map( f => (
						<li key={f.id}>
							<Text>{f.message || `${ formatCount( 1, 'item' ) } could not move (${ f.code })`}</Text>
						</li>
					) )}
					{failed > failures.length ? (
						<li><Text type='secondary'>{`and ${ failed - failures.length } more`}</Text></li>
					) : null}
				</ul>
			) : null}
		</DataState>
	);
}

/** The items an undo could not take back, read when the person asks. */
function UndoReport( { collectionId, taskId } ) {
	const [ report, setReport ] = useState( null );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );

	const load = useCallback( async () => {
		setLoading( true );
		setError( null );
		try {
			setReport( await getChangeCardUndo( collectionId, taskId ) );
		} catch ( e ) {
			setError( e );
		} finally {
			setLoading( false );
		}
	}, [ collectionId, taskId ] );

	useEffect( () => { load(); }, [ load ] );

	const failures = report?.failures || [];
	const failed = report?.undo?.progress?.failed || 0;
	const hasData = !! report;
	const isEmpty = hasData && ! failures.length;
	return (
		<DataState
			state={deriveDataState( { loading, error, hasData, isEmpty } )}
			hasData={hasData}
			isEmpty={isEmpty}
			skeleton={<Skeleton variant='line' count={2} />}
			empty={<Text type='secondary' className='imports-list__line'>Every item it worked through went back.</Text>}
			error={error}
			onRetry={load}
			label='what did not go back'
			className='imports-task__report'
		>
			<ul className='imports-plan__list'>
				{failures.map( f => (
					<li key={f.id}>
						<Text>{f.message || `${ formatCount( 1, 'item' ) } could not go back (${ f.code })`}</Text>
					</li>
				) )}
				{failed > failures.length ? (
					<li><Text type='secondary'>{`and ${ failed - failures.length } more`}</Text></li>
				) : null}
			</ul>
		</DataState>
	);
}

/**
 * One card change running as a background task, as a row of the Imports
 * tab (KPR-705; keepr-api docs/SCHEMA.md § Imports): who started it, when,
 * the two cards, its progress while it runs, what became of the items, how
 * it ended, and — on request — what did not move. It has no Undo import:
 * once it has finished, Undo card change (KPR-704) takes every item it moved
 * back, item by item, as a task of its own whose progress, counts and — on
 * request — items it could not take back show here too. `onChanged` reads
 * the list again.
 */
export default function ChangeCardTaskRow( { row, collectionId, who, onChanged } ) {
	const { modal, message } = App.useApp();
	const cards = useOptionalCardDefinitions();
	const [ open, setOpen ] = useState( false );
	const [ undoOpen, setUndoOpen ] = useState( false );
	const [ starting, setStarting ] = useState( false );
	const task = row.changeCard || {};
	const undo = task.undo || null;
	const undoProgress = undo?.progress || {};
	const undoPercent = undoProgress.total ? Math.min( 100, Math.round( ( ( undoProgress.done || 0 ) / undoProgress.total ) * 100 ) ) : 0;

	const startUndo = () => confirmDestructive( modal, {
		title: 'Undo this card change?',
		content: undoConfirmText( task ),
		okText: 'Undo card change',
		onOk: async () => {
			setStarting( true );
			try {
				await undoChangeCardTask( collectionId, task.id );
				message.info( 'Undoing the card change. This list shows its progress.' );
			} catch ( e ) {
				message.error( e?.response?.data?.message || 'The card change could not be undone. Nothing was changed.' );
			} finally {
				setStarting( false );
				if ( typeof onChanged === 'function' ) onChanged();
			}
		},
	} );
	const progress = task.progress || {};
	const status = task.running ? STATUS.pending : ( undo?.running ? UNDO_STATUS.running : ( undo?.status === 'done' ? UNDO_STATUS.done : STATUS[ task.status ] ) );
	const percent = progress.total ? Math.min( 100, Math.round( ( ( progress.done || 0 ) / progress.total ) * 100 ) ) : 0;
	const somethingLeft = ( progress.skipped || 0 ) + ( progress.failed || 0 ) > 0;
	// ⋯ → Undo card change…, once the change has finished and moved something.
	const menu = ! task.running && ( progressOf( task ).moved || 0 ) > 0 ? buildActionMenu( [
		{
			key: 'undo',
			group: 'destructive',
			icon: <ARROW_COUNTERCLOCKWISE />,
			label: 'Undo card change…',
			disabled: ! task.undoable || starting,
			title: task.undoable ? undefined : UNDO_WHY_WORDS[ task.undoWhy ],
			onClick: startUndo,
		},
	] ) : null;
	return (
		<>
			<div className='imports-list__main'>
				<div className='imports-list__name'>
					<Text strong>{who}</Text>
					{status ? <Tag color={status.color}>{status.label}</Tag> : null}
				</div>
				<Text type='secondary' className='imports-list__line'>
					{joinMeta( formatDisplayDateTime( task.startedAt || row.lastAt ), 'Change card' )}
				</Text>
				<div className='imports-task__cards'>
					<CardBadge card={task.card} cardsById={cards?.cardsById} />
					<ARROW_RIGHT aria-hidden='true' className='imports-task__arrow' />
					<span className='imports-task__sr'>to</span>
					<CardBadge card={task.target} cardsById={cards?.cardsById} />
				</div>
				{task.running ? (
					<>
						<Text className='imports-list__line' role='status'>{taskProgressLine( progress )}</Text>
						<Progress percent={percent} size='small' showInfo={false} className='imports-task__progress' aria-label={`${ percent }% worked through`} />
				</>
			) : null}
			<Text className='imports-list__line'>{taskCountsLine( progress )}</Text>
			{task.status === 'failed' && task.error ? (
				<Text type='danger' className='imports-list__line'>{task.error}</Text>
			) : null}
			{somethingLeft ? (
				<>
					<Button type='link' size='small' className='imports-task__toggle' aria-expanded={open} onClick={() => setOpen( v => ! v )}>
						{open ? 'Hide what did not move' : 'Show what did not move'}
					</Button>
					{open ? <TaskReport collectionId={collectionId} taskId={task.id} /> : null}
				</>
			) : null}
			{! task.running && ! undo && ( progress.moved || 0 ) > 0 ? (
				<ChangeCardSourceOffer collectionId={collectionId} card={task.card} />
			) : null}
			{undo ? (
				<>
					<Text type='secondary' className='imports-list__line'>
						{joinMeta( formatDisplayDateTime( undo.startedAt ), undo.running ? 'Undoing' : 'Undone' )}
					</Text>
					{undo.running ? (
						<Progress percent={undoPercent} size='small' showInfo={false} className='imports-task__progress' aria-label={`${ undoPercent }% undone`} />
					) : null}
					<Text className='imports-list__line' role={undo.running ? 'status' : undefined}>{undoCountsLine( undoProgress )}</Text>
					{undo.status === 'failed' && undo.error ? (
						<Text type='danger' className='imports-list__line'>{undo.error}</Text>
					) : null}
					{undoProgress.failed ? (
						<>
							<Button type='link' size='small' className='imports-task__toggle' aria-expanded={undoOpen} onClick={() => setUndoOpen( v => ! v )}>
								{undoOpen ? 'Hide what did not go back' : 'Show what did not go back'}
							</Button>
							{undoOpen ? <UndoReport collectionId={collectionId} taskId={task.id} /> : null}
						</>
					) : null}
				</>
			) : null}
		</div>
		{menu ? (
			<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={menu}>
				<Button
					icon={<KEBAB />}
					loading={starting}
					aria-label={`Actions for the card change of ${ formatDisplayDateTime( task.startedAt || row.lastAt ) }`}
				/>
			</Dropdown>
		) : null}
		</>
	);
}
